// src/v2/leases/LeaseDetailPage.jsx
//
// LEASE-DETAIL-HEADER-MERGE: Collapsed double header into one title band.
// LEASE-DETAIL-TABS-SHELL: Added 5-tab chrome (Overview · Terms · Financials · Dates · Intel).
//
// Layout:
//   Zone 1 (title band): "Leases:" prefix + bold city/premises headline + muted alias;
//     subline: lease ID (plain font) · type (plain text) · LeaseStatusBadge dropdown.
//   Zone 2 (chrome row): DetailUnifiedBar with 5 tabs; absolute-left
//     "‹ Leases" back-nav; rightSlot = prev/next pager + divider + "Edit lease".
//   Body: LeaseDetailBody wrapped in shared PAGE_CONTENT_MAX_WIDTH centered column,
//     with activeTab forwarded to control which pane renders.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation, Navigate } from "react-router-dom";
import { readRecordReturn, recordBackState } from "../../services/recordReturn.js";
import { buildLeaseFullUrl } from "../../services/buildUrl.js";
import { LAYERS, T } from "../../app/tokens.js";
import { LeaseDetailBody } from "./LeaseDetailBody.jsx";
import { TopBarControls, CenteredSearchAnchor } from "../Shell.jsx";
import { getLeaseById, updateLease } from "../../data/leasesClient.js";
import { showToast } from "../../components/toastService.jsx";
import { EditLeaseModal } from "../../features/leases/EditLeaseModal.jsx";
import { DetailUnifiedBar } from "../../components/DetailUnifiedBar.jsx";
import { DetailNavBand } from "../../components/DetailRecordChrome.jsx";
import { RecordLayout, RecordBackHost, RecordActionsHost } from "../../components/OneRowChrome.jsx";
import { LeaseNavPair } from "./LeaseNavPair.jsx";
import { PAGE_CONTENT_MAX_WIDTH } from "../../components/PageHeaderStack.jsx";
import { LeaseStatusBadge } from "../../components/LeaseStatusBadge.jsx";
import { LEASE_BUCKET_TO_DB_STATUS } from "../../features/leases/leaseStatusLabels.js";
import { NotFoundError } from "../../data/errors.js";
import { log } from "../../utils/logger.js";
import { useFeatureFlagContext } from "../../contexts/FeatureFlagContext.jsx";
import { loadLastTab, saveLastTab } from "../../services/tabStatePersistence.js";
import { HeaderAreaLabel } from "../../components/HeaderAreaLabel.jsx";

// ─── Tab definitions ─────────────────────────────────────────────────────────

const VALID_TABS = new Set(["overview", "financials", "dates", "documents", "intel"]);

// Maps feature_flags.key (surface='lease_tab') → the internal tab key and
// default display label used by LeaseDetailPage's activeTab state and tab strip.
//
// Key invariants:
//   - Internal keys match the VALID_TABS set and the keys used in LeaseDetailBody.
//   - Sort order comes from the DB (sort_order column); the order here is docs.
const LEASE_TAB_DB_KEY_MAP = {
  lease_tab_overview:   { key: "overview",   label: "Overview" },
  lease_tab_financials: { key: "financials", label: "Financials" },
  lease_tab_dates:      { key: "dates",      label: "Dates" },
  lease_tab_documents:  { key: "documents",  label: "Documents" },
  lease_tab_intel:      { key: "intel",      label: "Intel" },
};

function resolveTab(raw) {
  if (raw && VALID_TABS.has(raw)) return raw;
  return "overview";
}

// ─── rawToDisplayBucket ───────────────────────────────────────────────────
// Explicit persisted buckets are authoritative. The raw status map remains
// the compatibility fallback for older rows that have no lease_status value.
function rawToDisplayBucket(rawStatus, leaseStatus = null) {
  if (leaseStatus != null) return leaseStatus;
  switch (rawStatus) {
    case "active":         return "in_use";
    case "holdover":       return "in_use";
    case "in_negotiation": return "in_use";
    case "draft":          return "draft";
    case "sublease_out":   return "sublease_out";
    case "expired":        return "expired";
    case "terminated":     return "expired";
    default:               return null;
  }
}

// ─── Component ───────────────────────────────────────────────────────────────

export function LeaseDetailPage({
  currentRole,
  currentUser = null,
  flags = {},
  onRoleChange,
  onOpenSearch,
  onCloseSearch,
  onClearSearch,
  onQueryChange,
  searchInputRef,
  searchOpen,
  searchQuery,
  searchPalette = null,
  onSubtitleChange,
}) {
  const { leaseId, tab: tabParam } = useParams();
  const navigate                   = useNavigate();
  const routeLocation              = useLocation();
  const routeState                 = routeLocation.state;
  const recordReturn = readRecordReturn(routeState);
  const returnToRecord = () => recordReturn
    ? navigate(recordReturn.path, { state: recordBackState() })
    : navigate("/leases");
  const orderedIds = Array.isArray(routeState?.orderedIds) ? routeState.orderedIds : null;
  const detailRouteState = {
    ...(recordReturn ? { recordReturn } : {}),
    ...(orderedIds ? { orderedIds } : {}),
    ...(typeof routeState?.returnTo === "string" ? { returnTo: routeState.returnTo } : {}),
  };
  const hasDetailRouteState = Object.keys(detailRouteState).length > 0;

  // ADMIN-ADD-LEASES-TABS: derive visible tabs from DB-backed leaseTabs context.
  const { leaseTabs } = useFeatureFlagContext();

  const [activeTab,      setActiveTab]      = useState(() => resolveTab(tabParam));
  const [lease,          setLease]          = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [notFound,       setNotFound]       = useState(false);
  const [leaseEditOpen,  setLeaseEditOpen]  = useState(false);
  const [editableLease, setEditableLease] = useState(null);
  const [bodyRefreshKey, setBodyRefreshKey] = useState(0);
  const [leaseSyncError, setLeaseSyncError] = useState(null);
  const [areaContext, setAreaContext] = useState({ leaseId: null, label: null });

  // Documents tab count badge — lifted from LeaseDetailBody via callback.
  const [documentsCount, setDocumentsCount] = useState(null);

  // ADMIN-ADD-LEASES-TABS: build visible, ordered tab list from context.
  // Disabled tabs are excluded; order comes from DB sort_order.
  // The Documents tab gets the count badge applied here so it shows on the
  // tab label while using the context-driven tab list.
  const visibleTabs = useMemo(() => {
    return leaseTabs
      .filter((t) => t.enabled)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((t) => {
        const def = LEASE_TAB_DB_KEY_MAP[t.key];
        if (!def) return null;
        if (def.key === "documents") {
          const docLabel = documentsCount != null && documentsCount > 0
            ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                Documents
                <span
                  data-testid="documents-tab-badge"
                  style={{
                    display:        "inline-flex",
                    alignItems:     "center",
                    justifyContent: "center",
                    minWidth:        17,
                    height:          17,
                    padding:         "0 5px",
                    borderRadius:    999,
                    background:      T.blueSoft,
                    color:           T.blue,
                    fontSize:        "10.5px",
                    fontWeight:      T.fwSemibold,
                  }}
                >
                  {documentsCount}
                </span>
              </span>
            )
            : "Documents";
          return { key: def.key, label: docLabel };
        }
        return { key: def.key, label: def.label };
      })
      .filter(Boolean);
  }, [leaseTabs, documentsCount]);

  // Sync activeTab when the URL param changes (e.g. browser Back).
  useEffect(() => {
    setActiveTab(resolveTab(tabParam));
  }, [tabParam]);

  // ADMIN-ADD-LEASES-TABS: redirect to first visible tab if the current
  // activeTab has been disabled via the Admin feature-flag panel. This mirrors
  // the LocationDetail guard (location_tab surface). Runs only when visibleTabs
  // or activeTab changes; no-ops when leaseTabs are still loading (empty list).
  useEffect(() => {
    if (visibleTabs.length === 0) return;
    const isVisible = visibleTabs.some((t) => t.key === activeTab);
    if (!isVisible) {
      setActiveTab(visibleTabs[0].key);
    }
  }, [visibleTabs, activeTab]);

  // Clear the AppShell subtitle bar — the Zone 1 title band is the sole identity bar.
  useEffect(() => {
    if (typeof onSubtitleChange === "function") {
      onSubtitleChange("");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!leaseId) return;
    setLoading(true);
    setNotFound(false);
    getLeaseById(leaseId)
      .then((data) => {
        setLease(data);
        setLoading(false);
      })
      .catch((err) => {
        if (err instanceof NotFoundError) {
          setNotFound(true);
        } else {
          log.error("LeaseDetailPage: failed to load lease header", { leaseId, err: String(err) });
        }
        setLoading(false);
      });
  }, [leaseId]);

  // OBJECT-TABS-PERSIST-LAST-SELECTION: when the URL path has no tab segment,
  // restore the last-remembered tab for this lease from localStorage.
  // MUST be declared before the notFound early-return to satisfy Rules of Hooks.
  //
  // The guard ref is keyed by leaseId (not a plain boolean) so that prev/next
  // navigation that reuses the same mounted component for a different lease
  // resets the guard and re-runs the restore for the new lease. This mirrors
  // the pattern used in LocationDetail.
  //
  // Reads loadLastTab directly (not from activeTab state) to avoid a stale
  // closure when the effect fires immediately after a leaseId change, before
  // useTabState's instanceId-change effect has updated activeTab.
  const leaseTabRestoredForRef = useRef(null);
  useEffect(() => {
    // Wait until visible tabs are loaded and leaseId is present.
    if (!leaseId || visibleTabs.length === 0) return;
    // Already handled this leaseId — skip tab-click re-renders.
    if (leaseTabRestoredForRef.current === leaseId) return;
    leaseTabRestoredForRef.current = leaseId;
    // If a tab path segment is in the URL, honour it; don't override with memory.
    if (tabParam) return;
    // If this navigation was driven by Prev/Next, the current tab is already
    // correct in the URL; skip the memory restore so we don't jump away.
    if (routeState?.skipTabRestore) return;
    // Read memory directly to avoid stale activeTab on cross-lease navigation.
    const remembered = loadLastTab("lease", leaseId);
    const validLeaseTabKeys = visibleTabs.map((t) => t.key);
    if (remembered && validLeaseTabKeys.includes(remembered) && remembered !== "overview") {
      const state = hasDetailRouteState ? detailRouteState : undefined;
      navigate(buildLeaseFullUrl(leaseId, remembered, routeLocation.search), { replace: true, state });
    }
  }, [leaseId, visibleTabs]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Pager IDs (computed before early return so hooks below are unconditional) ─
  const currentIdx  = orderedIds ? orderedIds.indexOf(leaseId) : -1;
  const prevLeaseId = orderedIds && currentIdx > 0 ? orderedIds[currentIdx - 1] : null;
  const nextLeaseId = orderedIds && currentIdx >= 0 && currentIdx < orderedIds.length - 1
    ? orderedIds[currentIdx + 1]
    : null;

  // Stable ref so the keyboard effect always calls the latest handlePrev/Next
  // without needing them in its dependency array (they close over state).
  const navHandlersRef = useRef({});

  // ── Arrow-key keyboard navigation ─────────────────────────────────────────
  // Declared before the notFound early return to satisfy Rules of Hooks.
  useEffect(() => {
    if (!orderedIds) return;
    function isInputFocused() {
      const el = document.activeElement;
      if (!el) return false;
      const tag = el.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || el.isContentEditable === true;
    }
    function handleKey(e) {
      if (isInputFocused()) return;
      if (e.key === "ArrowLeft")  { e.preventDefault(); navHandlersRef.current.handlePrev?.(); }
      if (e.key === "ArrowRight") { e.preventDefault(); navHandlersRef.current.handleNext?.(); }
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [prevLeaseId, nextLeaseId, orderedIds]);

  const handleAreaLabelChange = useCallback((sourceLeaseId, label) => {
    if (sourceLeaseId === leaseId) {
      setAreaContext({ leaseId: sourceLeaseId, label: label || null });
    }
  }, [leaseId]);

  if (notFound) return <Navigate to="/leases" replace />;

  const displayBucket = lease ? rawToDisplayBucket(lease.status, lease.leaseStatus) : null;
  const isAdmin       = currentRole === "admin";

  // Header metadata is intentionally incomplete. The editor must use the
  // pinned body's full read, never blanks inferred from omitted header fields.
  const leaseForModal = lease && editableLease?.id === lease.id
    ? { ...lease, ...editableLease, type: editableLease.type ?? lease.leaseType }
    : null;
  const coveredSpaceLabel = areaContext.leaseId === leaseId ? areaContext.label : null;

  // ── Tab change handler ────────────────────────────────────────────────────
  function handleTabChange(key) {
    if (key === activeTab) return;
    setActiveTab(key);
    // OBJECT-TABS-PERSIST-LAST-SELECTION: persist tab selection for this lease.
    saveLastTab("lease", key, leaseId);
    const state = hasDetailRouteState ? detailRouteState : undefined;
    // Overview omits the tab segment so the canonical URL stays clean.
    if (key === "overview") {
      navigate(buildLeaseFullUrl(leaseId, "overview", routeLocation.search), { replace: false, state });
    } else {
      navigate(buildLeaseFullUrl(leaseId, key, routeLocation.search), { replace: false, state });
    }
  }

  // ── Status change handler ─────────────────────────────────────────────────
  async function handleStatusChange(bucket) {
    if (!lease) return;
    setLeaseSyncError(null);
    const dbStatus = LEASE_BUCKET_TO_DB_STATUS[bucket];
    const updated = await updateLease(
      lease.locationId,
      leaseId,
      { status: dbStatus, leaseStatus: bucket },
      { before: lease }
    );
    setLease(updated);
    try {
      const confirmed = await getLeaseById(leaseId);
      if (confirmed?.leaseStatus !== bucket) {
        throw new Error(`Lease status confirmation mismatch: expected ${bucket}`);
      }
      setLease(confirmed);
      showToast("Lease status updated", { type: "success" });
    } catch (err) {
      log.error("LeaseDetailPage: status confirmation failed", { leaseId, err: String(err) });
      setLeaseSyncError("Lease status saved, but confirmation could not be completed. The saved value remains displayed.");
      throw err;
    }
  }

  // ── Sub-nav rightSlot: LeaseNavPair (from nav state) — hidden on direct entry ──
  const CAN_WRITE = currentRole === "editor" || currentRole === "admin";

  function handlePrev() {
    if (!prevLeaseId) return;
    navigate(buildLeaseFullUrl(prevLeaseId, activeTab, routeLocation.search), {
      state: { ...detailRouteState, skipTabRestore: true },
    });
  }
  function handleNext() {
    if (!nextLeaseId) return;
    navigate(buildLeaseFullUrl(nextLeaseId, activeTab, routeLocation.search), {
      state: { ...detailRouteState, skipTabRestore: true },
    });
  }

  // Keep ref current so the keydown listener always calls the latest closures.
  navHandlersRef.current = { handlePrev, handleNext };

  const rightSlot = (
    <>
      {CAN_WRITE && (
        <>
          <div
            data-testid="lease-detail-edit-divider"
            style={{
              width:      1,
              alignSelf:  "stretch",
              background: T.planCompBorder,
              margin:     "6px 4px",
            }}
          />
          <button
            type="button"
            data-chrome-action="edit"
            data-testid="lease-detail-edit-button"
            onClick={() => setLeaseEditOpen(true)}
            disabled={!leaseForModal}
            style={{
              marginLeft:  0,
              background:  "transparent",
              border:      "none",
              color:       T.blue,
              padding:     "6px 8px",
              borderRadius: 6,
              fontSize:    T.fsXs,
              fontWeight:  T.fwSemibold,
              cursor:      "pointer",
              flexShrink:  0,
            }}
          >
            Edit lease
          </button>
        </>
      )}
      {orderedIds && (
        <LeaseNavPair prevLeaseId={prevLeaseId} nextLeaseId={nextLeaseId}
          onPrev={handlePrev} onNext={handleNext} />
      )}
    </>
  );

  return (
    <RecordLayout className="object-detail-layout">

      {/* ── Zone 1: Title band ───────────────────────────────────────────── */}
      <div
        data-testid="location-zone-title"
        className={`v2-detail-header object-title-row object-title-two-line${searchOpen ? " is-search-open" : ""}`}
        style={{
          background: T.card,
          borderBottom: `1px solid ${T.border}`,
          flexShrink: 0,
          position: "relative",
          zIndex: searchOpen ? LAYERS.modal : LAYERS.stickyChrome,
        }}
      >
        <RecordBackHost sectionLabel="Leases" backLabel={recordReturn?.label ?? "Leases"} onBack={returnToRecord} testId="lease-detail-back" />
        <div data-testid="location-object-header" className="object-title-content">
          {/* Line 1: bold city/premises · alias  |  right: RoleSwitcher + user chip */}
          <div style={{ display: "flex", alignItems: "center" }}>
            <div className="v2-detail-identity v2-header-line v2-header-title" title={[lease?.city || lease?.locationId, lease?.locationAlias, coveredSpaceLabel].filter(Boolean).join(" · ")}>
            <span
              style={{
                fontSize:      T.fsLg5,
                fontWeight:    T.fwBold,
                color:         T.dark,
                letterSpacing: -0.3,
              }}
            >
              {loading ? "—" : (lease?.city || lease?.locationId || "—")}
            </span>
            {!loading && lease?.locationAlias && (
              <>
                <span style={{ fontSize: T.fsMd, fontWeight: T.fwRegular, color: T.borderDark, margin: "0 7px" }}>·</span>
                <span style={{ fontSize: T.fsMd, color: T.light, fontWeight: T.fwRegular }}>{lease.locationAlias}</span>
              </>
            )}
            {!loading && coveredSpaceLabel && (
              <>
                <span style={{ fontSize: T.fsMd, fontWeight: T.fwRegular, color: T.borderDark, margin: "0 7px",
                  display: coveredSpaceLabel?.includes(" expired)") ? "none" : undefined }}>·</span>
                <HeaderAreaLabel testId="lease-detail-area-title">
                  {coveredSpaceLabel}
                </HeaderAreaLabel>
              </>
            )}
            </div>

          </div>

          {/* Line 2: lease ID (plain font) · type (plain text) · LeaseStatusBadge */}
          <div
            data-testid="location-header-identity-strip"
            className="object-title-meta"
            style={{ fontSize: T.fsXs, color: T.mid, marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}
          >
            <span
              data-testid="lease-detail-id"
              style={{ fontWeight: T.fwSemibold }}
            >
              {loading ? "—" : (leaseId ?? "—")}
            </span>

            {!loading && lease && (
              <LeaseStatusBadge
                status={displayBucket}
                canWrite={CAN_WRITE}
                onChange={handleStatusChange}
              />
            )}
          </div>
        </div>
        <div className="v2-detail-controls" style={{ paddingRight: 18 }}>
          <RecordActionsHost />
          <CenteredSearchAnchor
            onOpenSearch={onOpenSearch} onCloseSearch={onCloseSearch}
            onClearSearch={onClearSearch} onQueryChange={onQueryChange}
            searchInputRef={searchInputRef} searchOpen={searchOpen}
            searchQuery={searchQuery} searchPalette={searchPalette}
          />
          <TopBarControls currentUser={currentUser} currentRole={currentRole}
            flags={flags} onRoleChange={onRoleChange} showSearch={false} />
        </div>
      </div>
      {leaseSyncError && (
        <div
          data-testid="lease-status-sync-error"
          role="alert"
          style={{
            padding: "7px 18px",
            background: T.warmBeige,
            color: T.dark,
            borderBottom: `1px solid ${T.border}`,
            fontSize: T.fsXs,
          }}
        >
          {leaseSyncError}
        </div>
      )}

      <div data-testid="lease-detail-scroll-body" className="object-detail-body" style={{ paddingBottom: 64 }}>
      {/* Tabs belong to the gray scrolling content, not the app header. */}
      <DetailNavBand
        enabled={false}
        data-testid="location-zone-tabs"
      >
        <DetailUnifiedBar
          oneRow
          recordNavigation
          testId="lease-detail-unified-bar"
          barContentTestId="lease-detail-bar-content"
          backLabel={recordReturn?.label ?? "Leases"}
          onBack={returnToRecord}
          backTestId="lease-detail-back"
          tabs={visibleTabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          rightSlot={rightSlot}
        />
      </DetailNavBand>

      {/* ── Body: LeaseDetailBody ────────────────────────────────────────── */}
        {loading && <div role="status" data-testid="lease-detail-loading" style={{ padding: 32, color: T.mid }}>Loading lease…</div>}
        {lease && (
          <div className="object-detail-entry" style={{ maxWidth: PAGE_CONTENT_MAX_WIDTH, margin: "0 auto", paddingInline: 32 }}>
            <LeaseDetailBody
              leaseId={leaseId}
              locationId={lease.locationId}
              currentRole={currentRole}
              refreshKey={bodyRefreshKey}
              onLeaseLoaded={setEditableLease}
              activeTab={activeTab}
              onDocumentsCount={setDocumentsCount}
              onTabChange={handleTabChange}
              onAreaLabelChange={handleAreaLabelChange}
            />
          </div>
        )}
      </div>

      {/* ── Edit lease modal ─────────────────────────────────────────────── */}
      {CAN_WRITE && leaseForModal && (
        <EditLeaseModal
          isOpen={leaseEditOpen}
          onClose={() => setLeaseEditOpen(false)}
          lease={leaseForModal}
          canWrite={CAN_WRITE}
          isAdmin={isAdmin}
          onSaved={(savedLease) => {
            setLeaseSyncError(null);
            setLeaseEditOpen(false);
            // Apply the canonical PATCH response immediately so the modal and
            // header show the confirmed value before the follow-up read.
            if (savedLease?.id) setLease(savedLease);
            // Refresh the header and trigger a body re-fetch after a successful save.
            getLeaseById(leaseId)
              .then((confirmed) => {
                if (
                  savedLease?.leaseStatus != null &&
                  confirmed?.leaseStatus !== savedLease.leaseStatus
                ) {
                  log.error("LeaseDetailPage: modal status confirmation mismatch", {
                    leaseId,
                    expected: savedLease.leaseStatus,
                    actual: confirmed?.leaseStatus ?? null,
                  });
                  setLeaseSyncError("Lease status saved, but the confirmation read returned a different value. The saved value remains displayed.");
                  return;
                }
                setLease(confirmed);
              })
              .catch((err) => {
                log.error("LeaseDetailPage: modal status confirmation failed", { leaseId, err: String(err) });
                setLeaseSyncError("Lease saved, but confirmation could not be completed. The saved value remains displayed.");
              });
            setBodyRefreshKey((k) => k + 1);
          }}
          onDeleted={() => navigate("/leases")}
        />
      )}
    </RecordLayout>
  );
}
